import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MdCheckCircle } from "react-icons/md";
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti";

const TransactionDetailModal = ({ isOpen, onClose, transaction, wallet }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  if (!isOpen || !transaction) return null;

  const isCredit = transaction.type === "credit" || transaction.type === "refund";

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-emerald-50 border-emerald-200 text-emerald-700";
      case "pending":
      case "processing":
        return "bg-amber-50 border-amber-200 text-amber-700";
      case "failed":
      case "cancelled":
        return "bg-red-50 border-red-200 text-red-700";
      default:
        return "bg-blue-50 border-blue-200 text-blue-700";
    }
  };

  const getHeaderGradient = (status) => {
    switch (status) {
      case "completed":
        return "from-emerald-500 to-teal-600";
      case "pending":
      case "processing":
        return "from-amber-500 to-orange-600";
      case "failed":
      case "cancelled":
        return "from-red-500 to-rose-600";
      default:
        return "from-blue-500 to-cyan-600";
    }
  };

  const modalContent = (
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
      onClick={handleClose}
    >
      <div
        className={`bg-white dark:bg-slate-900 w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className={`px-5 py-4 bg-gradient-to-r ${getHeaderGradient(transaction.status)} text-white flex items-center gap-3 rounded-t-3xl md:rounded-t-3xl`}>
          <button
            onClick={handleClose}
            className="text-white text-2xl leading-none hover:opacity-80 transition"
          >
            ←
          </button>
          <div className="flex-1">
            <div className="font-semibold text-lg">Transaction Details</div>
            <p className="text-white/70 text-xs font-mono mt-0.5">{transaction.transactionId}</p>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Amount Card */}
          <div className={`p-5 rounded-2xl bg-gradient-to-br ${getHeaderGradient(transaction.status).replace("from-", "from-").replace("to-", "to-")} text-white`}>
            <p className="text-xs font-semibold uppercase tracking-widest opacity-80 mb-2">
              {isCredit ? "Amount Received" : "Amount Paid"}
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black">
                {wallet?.currency === "USD" ? "$" : "₹"}
                {transaction.amount}
              </span>
              <span className="text-sm font-semibold opacity-80">{transaction.currency}</span>
            </div>
          </div>

          {/* Status & Type */}
          <div className="grid grid-cols-2 gap-3">
            {/* Status */}
            <div className={`p-4 rounded-2xl border-2 ${getStatusColor(transaction.status)}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">Status</p>
              <div className="flex items-center gap-2">
                {transaction.status === "completed" && <MdCheckCircle className="w-5 h-5" />}
                <span className="text-sm font-bold capitalize">{transaction.status}</span>
              </div>
            </div>

            {/* Category */}
            <div className="p-4 rounded-2xl border-2 border-slate-200 dark:border-slate-700">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400 opacity-70 mb-2">
                Category
              </p>
              <span className="text-sm font-bold text-slate-900 dark:text-white capitalize">
                {transaction.category?.replace(/_/g, " ")}
              </span>
            </div>
          </div>

          {/* Description */}
          {transaction.description && (
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-200 dark:border-slate-700">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-2">
                Description
              </p>
              <p className="text-sm text-slate-900 dark:text-white font-medium">{transaction.description}</p>
            </div>
          )}

          {/* Balance Before & After */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border-2 border-blue-100 dark:border-blue-500/20">
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-2">
                Balance Before
              </p>
              <p className="text-lg font-black text-blue-900 dark:text-blue-100">
                {wallet?.currency === "USD" ? "$" : "₹"}
                {transaction.balanceBefore}
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border-2 border-emerald-100 dark:border-emerald-500/20">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-2">
                Balance After
              </p>
              <p className="text-lg font-black text-emerald-900 dark:text-emerald-100">
                {wallet?.currency === "USD" ? "$" : "₹"}
                {transaction.balanceAfter}
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-3">Timeline</p>
            <div className="space-y-2">
              {/* Initiated */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-600 dark:text-slate-400">Initiated</p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white mt-1">
                    {new Date(transaction.initiatedAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {new Date(transaction.initiatedAt).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>

              {/* Completed */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">Completed</p>
                  <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mt-1">
                    {new Date(transaction.completedAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    {new Date(transaction.completedAt).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>

              {/* Created */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="w-2 h-2 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-600 dark:text-slate-400">Created</p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white mt-1">
                    {new Date(transaction.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {new Date(transaction.createdAt).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Gateway */}
          {transaction.paymentGateway && (
            <div className="p-4 rounded-2xl bg-purple-50 dark:bg-purple-500/10 border-2 border-purple-100 dark:border-purple-500/20">
              <p className="text-[10px] font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400 mb-3">
                Payment Method
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-purple-900 dark:text-purple-100 capitalize">
                  {transaction.paymentGateway?.provider}
                </span>
                <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 bg-white dark:bg-purple-900/30 px-2.5 py-1 rounded-full">
                  Connected
                </span>
              </div>
            </div>
          )}

          {/* Notes */}
          {transaction.notes && (
            <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-100 dark:border-amber-500/20">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-2">
                Notes
              </p>
              <p className="text-sm text-amber-900 dark:text-amber-100">{transaction.notes}</p>
            </div>
          )}

          {/* Metadata */}
          {/* {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
            <div className="p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 border-2 border-indigo-100 dark:border-indigo-500/20">
              <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400 mb-3">
                Additional Details
              </p>
              <div className="space-y-2">
                {Object.entries(transaction.metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-start gap-2 py-2 px-2 rounded-lg bg-white/50 dark:bg-indigo-900/20">
                    <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 capitalize">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </span>
                    <span className="text-xs font-medium text-indigo-900 dark:text-indigo-100 text-right break-words max-w-[150px]">
                      {typeof value === "object" ? JSON.stringify(value) : value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Transaction ID */}
          <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 font-mono">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-2">
              Transaction ID
            </p>
            <p className="text-xs font-mono text-slate-900 dark:text-slate-100 font-semibold break-all">
              {transaction.transactionId}
            </p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3.5 border-2 border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-2xl font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleClose}
            className="flex-1 py-3.5 bg-primaryColor text-white rounded-2xl font-semibold hover:shadow-lg transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TransactionDetailModal;